import { Swiper } from 'swiper';
import { SwiperOptions } from 'swiper/types';
import { Navigation, Pagination, Autoplay } from 'swiper/modules'

Swiper.use([Navigation, Pagination, Autoplay]);

const _swiperOptions: any = {
  navigation: {
    nextEl: '.swiper-button-next',
    prevEl: '.swiper-button-prev',
  },
  pagination: {
    el: '.swiper-pagination',
    type: 'bullets',
    clickable: true,
  }
}

const defaultSwiperOptions: SwiperOptions = {
  a11y: {
    firstSlideMessage: 'First slide',
    lastSlideMessage: 'Last slide',
    nextSlideMessage: 'Next slide',
    prevSlideMessage: 'Previous slide',
  },
  centerInsufficientSlides: true,
  centeredSlidesBounds: true,
  direction: 'horizontal',
  loop: false,
  navigation: true,
  on: {
    init: function (swiper) {
      wfUpdateActiveBackgroundItem(swiper);
      wfUpdateActiveContentSyncItem(swiper);
      wfUpdateActiveNavbarButton(swiper);
      wfAddKbSupportPagination(swiper)
    },
    slideChange: function (swiper) {
      wfUpdateActiveBackgroundItem(swiper);
      wfUpdateActiveContentSyncItem(swiper);
      wfUpdateActiveNavbarButton(swiper);
    },
  },
  pagination: true,
  slideVisibleClass: 'swiper-slide-visible',
  slidesPerView: 1,
  spaceBetween: 20,
  speed: 500,
  watchOverflow: true,
  watchSlidesProgress: true,
};

// Add Keyboard support for pagination bullets
function wfAddKbSupportPagination(swiperInstance: Swiper) {
  let paginationList = swiperInstance.el.closest('.page-component')?.querySelectorAll<HTMLElement>('.swiper-pagination');

  if (!paginationList) {
    return;
  }

  paginationList.forEach((pagination) => {
    // Get anchor name for the aria-controls attribute
    const anchorId = pagination.getAttribute('data-anchor');
    // Set indexes for all of the bullets
    const bullets = pagination.querySelectorAll('.swiper-pagination-bullet');

    const paginationLength = bullets.length;

    bullets.forEach((bullet, index) => {
      bullet.setAttribute('tabindex', '-1');
      bullet.setAttribute('data-index', (index + 1).toString());
      bullet.setAttribute('aria-controls', `slide-${anchorId}-${index + 1}`);
      bullet.ariaLabel = `Slide ${index + 1}`;

    });

    // Only add the Event Listener if there is more than one slide
    if (paginationLength > 1) {
      pagination.addEventListener('keydown', (event: KeyboardEvent) => {

        let activeBulletIndex = parseInt(pagination.querySelector('.swiper-pagination-bullet-active').getAttribute('data-index'));

        if (event.key === 'ArrowLeft') {
          if (activeBulletIndex === 1) {
            swiperInstance.slideToLoop(paginationLength - 1);
          }
          else {
            swiperInstance.slideToLoop(activeBulletIndex - 2);
          }
        }

        if (event.key === 'ArrowRight') {

          if (activeBulletIndex === paginationLength) {
            swiperInstance.slideToLoop(0);
          }
          else {
            swiperInstance.slideToLoop(activeBulletIndex);
          }
        }
      });
    }
  });
}

function wfUpdateActiveNavbarButton(swiperInstance: Swiper) {
  let navbars = swiperInstance.el.closest('.page-component')?.querySelectorAll<HTMLElement>('.swiper-navbar');
  if (navbars) {
    navbars.forEach((navbar) => {
      const allNavbarButtons = navbar.querySelectorAll('.swiper-navbar-button');
      allNavbarButtons.forEach((button) => {
        button.classList.remove('swiper-navbar-button-active');
      });
      const currentNavbarButton = allNavbarButtons.item(swiperInstance.realIndex);
      if (currentNavbarButton) {
        currentNavbarButton.classList.add('swiper-navbar-button-active');
      }
    });
  }
}

function wfUpdateActiveBackgroundItem(swiperInstance: Swiper) {
  let background = swiperInstance.el.closest('.page-component')?.querySelector<HTMLElement>('.swiper-background');
  if (background) {
    let backgroundItems = background.querySelectorAll<HTMLElement>('.swiper-background-item');
    backgroundItems.forEach((item) => {
      item.classList.remove('swiper-background-item-active');
    });
    const currentBackgroundItem = backgroundItems.item(swiperInstance.realIndex);
    if (currentBackgroundItem) {
      currentBackgroundItem.classList.add('swiper-background-item-active');
    }
  }
}

function wfUpdateActiveContentSyncItem(swiperInstance: Swiper) {
  let contentSync = swiperInstance.el.closest('.page-component')?.querySelectorAll<HTMLElement>('.swiper-content-sync');
  if (contentSync) {
    contentSync.forEach((sync) => {
      let allSyncItems = sync.querySelectorAll<HTMLElement>('.swiper-content-sync-item');
      allSyncItems.forEach((item) => {
        item.classList.remove('active');
      });
      let currentSyncItem = allSyncItems.item(swiperInstance.realIndex);
      if (currentSyncItem) {
        currentSyncItem.classList.add('active');
      }
    });
  }
}

function queryAllElements(selector): HTMLElement[] {
  const allElements = [];

  function searchNodes(node) {
    if (node instanceof HTMLElement) {
      if (node.matches(selector)) {
        allElements.push(node);
      }

      if (node.shadowRoot) {
        node.shadowRoot.childNodes.forEach(searchNodes);
      }

      node.childNodes.forEach(searchNodes);
    }
  }

  searchNodes(document.body);

  return allElements;
}

function initializeSwiper(wrapperSelector: string, additionalOptions?: SwiperOptions): Swiper[] {
  const wrapperElements = queryAllElements(wrapperSelector);
  const swipers: Swiper[] = [];

  wrapperElements?.forEach((wrapperElement: HTMLElement) => {
    const swiperElement = wrapperElement.querySelector<HTMLElement>('.swiper');
    if (swiperElement) {
      const swiperDataOptions = JSON.parse(swiperElement.dataset.swiper || '{}');
      const options: SwiperOptions = { ...defaultSwiperOptions, ...additionalOptions, ...swiperDataOptions };

      if (options.autoplay) {
        options.autoplay = {
          delay: 4000,
          pauseOnMouseEnter: true
        };
      }

      if (options.pagination) {
        options.pagination = {
          el: wrapperElement.querySelector<HTMLElement>(_swiperOptions.pagination.el)!,
          type: _swiperOptions.pagination.type,
          clickable: _swiperOptions.pagination.clickable,
        };
      }

      if (options.navigation) {
        options.navigation = {
          prevEl: wrapperElement.querySelector<HTMLElement>(_swiperOptions.navigation.prevEl)!,
          nextEl: wrapperElement.querySelector<HTMLElement>(_swiperOptions.navigation.nextEl)!,
        };
      }

      if (options.watchOverflow && swiperElement.querySelectorAll('.swiper-slide').length < 2) {
        options.loop = false;
      }

      const swiper = new Swiper(swiperElement, options);
      swipers.push(swiper);
    }
  });

  return swipers;
}

export function initializeSwiperInstances(): void {
  initializeSwiper('.hero-slider', {
    slidesPerView: 1,
    spaceBetween: 0,
    loop: true,
    effect: 'fade',
    fadeEffect: {
      crossFade: true,
    },
  });

  initializeSwiper('.cta-icon-slider', {
    slidesPerView: 'auto',
    spaceBetween: 0
  });

  initializeSwiper('.cards-slider', {
    slidesPerView: 'auto',
    spaceBetween: 0
  });

  initializeSwiper('.icons-carousel', {
    slidesPerView: 'auto',
    spaceBetween: 0
  });

  initializeSwiper('.image-carousel', {
    slidesPerView: 'auto',
    spaceBetween: 0
  });

  initializeSwiper('.testimonials-slider', {
    slidesPerView: 'auto',
    spaceBetween: 0
  });

  initializeSwiper('.corporate-members-slider', {
    slidesPerView: 'auto',
    spaceBetween: 0,
    loop: true,
    autoplay: true
  });

  initializeSwiper('.featured-events', {
    slidesPerView: 'auto',
    spaceBetween: 0
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initializeSwiperInstances();
});

// logic for Umbraco backend initialization
if (!(window as any).wakefly) {
  (window as any).wakeflyFunctions = [];
}
(window as any).wakeflyFunctions.push(initializeSwiperInstances);
